import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, Music2 } from 'lucide-react';
import { companyInfo } from '../data/mock';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div>
            <h3 className="text-white text-2xl font-bold mb-4">{companyInfo.name}</h3>
            <p className="text-gray-400 mb-4">
              Votre expert en réparation d'appareils électroniques à Villeurbanne depuis 10 ans.
            </p>
            <div className="flex space-x-3">
              <a
                href={companyInfo.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a
                href={companyInfo.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-600 transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a
                href={companyInfo.social.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-black transition-colors"
                aria-label="TikTok"
              >
                <Music2 size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-4">Liens Rapides</h4>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => scrollToSection('hero')}
                  className="hover:text-white transition-colors"
                >
                  Accueil
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('services')}
                  className="hover:text-white transition-colors"
                >
                  Nos Services
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('process')}
                  className="hover:text-white transition-colors"
                >
                  Comment ça marche
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('faq')}
                  className="hover:text-white transition-colors"
                >
                  FAQ
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="hover:text-white transition-colors"
                >
                  Contact
                </button>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-4">Nos Services</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/reparation-iphone-villeurbanne" className="hover:text-white transition-colors">
                  Réparation iPhone
                </Link>
              </li>
              <li>
                <Link to="/reparation-samsung-villeurbanne" className="hover:text-white transition-colors">
                  Réparation Samsung
                </Link>
              </li>
              <li>
                <Link to="/reparation-batterie-villeurbanne" className="hover:text-white transition-colors">
                  Remplacement Batterie
                </Link>
              </li>
              <li>
                <Link to="/reparation-ordinateur-villeurbanne" className="hover:text-white transition-colors">
                  Réparation PC & Mac
                </Link>
              </li>
              <li>
                <Link to="/reparation-tablette-villeurbanne" className="hover:text-white transition-colors">
                  Réparation Tablettes
                </Link>
              </li>
              <li>
                <Link to="/micro-soudure-villeurbanne" className="hover:text-white transition-colors">
                  Micro-soudure
                </Link>
              </li>
              <li className="hover:text-white transition-colors cursor-pointer">Diagnostic Gratuit</li>
            </ul>
          </div>

          {/* Zones desservies — SEO local */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-4">Zones desservies</h4>
            <ul className="space-y-1 text-sm">
              <li className="text-gray-400 font-semibold">Villeurbanne (69100)</li>
              <li className="hover:text-white transition-colors">Lyon 1er – 8e arrondissement</li>
              <li className="hover:text-white transition-colors">Bron · Vaulx-en-Velin</li>
              <li className="hover:text-white transition-colors">Caluire-et-Cuire · Décines</li>
              <li className="hover:text-white transition-colors">Meyzieu · Rillieux-la-Pape</li>
              <li className="hover:text-white transition-colors">Saint-Priest · Vénissieux</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPin className="flex-shrink-0 mt-1" size={18} />
                <span className="text-sm">{companyInfo.address}</span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="flex-shrink-0" size={18} />
                <div className="text-sm">
                  <a href={`tel:${companyInfo.phoneFormatted}`} className="hover:text-white block">
                    {companyInfo.phone}
                  </a>
                  <a href={`tel:${companyInfo.phone2Formatted}`} className="hover:text-white block">
                    {companyInfo.phone2}
                  </a>
                </div>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="flex-shrink-0" size={18} />
                <a href={`mailto:${companyInfo.email}`} className="text-sm hover:text-white">
                  {companyInfo.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-500">
              © {currentYear} {companyInfo.name}. Tous droits réservés.
            </p>
            <div className="flex space-x-6 text-sm">
              <Link to="/mentions-legales" className="hover:text-white transition-colors">
                Mentions légales
              </Link>
              <Link to="/politique-confidentialite" className="hover:text-white transition-colors">
                Politique de confidentialité
              </Link>
              <Link to="/termes-conditions" className="hover:text-white transition-colors">
                Termes et conditions
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
